  import React, { useState } from 'react';
  import {
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
  } from '@mui/material';
  import {
    Assessment as AssessmentIcon,
    AccessTime as TimeIcon,
    CalendarMonth as CalendarIcon,
    PriorityHigh as PriorityIcon,
  } from '@mui/icons-material';
  import AssessmentModal from './AssessmentModal';

  interface AssessmentCardProps {
    title: string;
    description: string;
    duration: string;
    meetingDetails: string;
    meetingSummary: string;
    goals: string[];
    companyName: string;
    companyIndustry: string;
    personaName: string;
    personaRole: string;
    companyLocations: string;
    assignmentId: number;
    onStart?: () => void;
    assessmentId?: string;
    status?: string;
    endDate: Date;
    startDate: Date;
  }

  function getDaysTill(endDate: string | Date | undefined): number | null {
    if (!endDate) return null;
    const dueCopy = new Date(endDate);
    if (isNaN(dueCopy.getTime())) return null;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    dueCopy.setHours(0, 0, 0, 0);
    const msPerDay = 1000 * 60 * 60 * 24;
    return Math.ceil((dueCopy.getTime() - now.getTime()) / msPerDay);
  }

  function formatShortDate(date: string | Date | undefined) {
    if (!date) return "—";
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return "—";
    return dateObj.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  const AssessmentCard: React.FC<AssessmentCardProps> = ({
    title,
    description,
    duration,
    meetingDetails,
    meetingSummary,
    goals,
    companyName,
    companyIndustry,
    personaName,
    personaRole,
    companyLocations,
    assignmentId,
    onStart,
    assessmentId,
    status,
    endDate,
    startDate,
  }) => {
    const [open, setOpen] = useState(false);
    let isAvailable = true;
    let dueLabel = '';
    let daysTill = getDaysTill(startDate);
    if (daysTill! > 0) {
        dueLabel = `Assessment starts in ${daysTill} day${daysTill! > 1 ? 's' : ''}`
        isAvailable = false
    } else {
      daysTill = getDaysTill(endDate);
      if (daysTill! > 0) {
        dueLabel = `Due in ${daysTill} day${daysTill! > 1 ? 's' : ''}`;
      } else if (daysTill === 0) {
        dueLabel = 'Due today';
      } else {
        dueLabel = `Past due by ${Math.abs(daysTill!)} day${Math.abs(daysTill!) > 1 ? 's' : ''}`;
      }
    }

    const showDuePill = daysTill !== null && daysTill <= 30;
    const isCompleted = status === 'completed';

    const handleCardClick = () => setOpen(true);

    return (
      <Box sx={{ position: 'relative', width: '100%' }}>
        {isCompleted ? (
          <Chip
            label="Completed"
            color="success"
            sx={{
              position: 'absolute',
              top: -18,
              right: 16,
              zIndex: 2,
              fontWeight: 600,
              fontSize: 14,
              borderRadius: '12px',
              background: 'linear-gradient(90deg, #43e97b 0%, #38f9d7 90%)',
              color: 'white',
              boxShadow: 2,
              pointerEvents: 'none',
              letterSpacing: 1
            }}
          />
        ) : showDuePill && (
          <Chip
            icon={<PriorityIcon sx={{ fontSize: 18 }}/>}
            label={dueLabel}
            color="warning"
            sx={{
              position: 'absolute',
              top: -18,
              right: 16,
              zIndex: 2,
              fontWeight: 600,
              fontSize: 14,
              borderRadius: '12px',
              background: 'linear-gradient(90deg, rgba(255,0,192,0.9) 0%, rgba(255,109,21,0.9) 90%)',
              color: 'white',
              boxShadow: 2,
              pointerEvents: 'none'
            }}
          />
        )}
        <Card
          onClick={handleCardClick}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            position: 'relative',
            boxShadow: 2,
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 4,
              transition: 'all 0.2s ease-in-out',
            }
          }}
        >
          {/* Left column: Icon */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 96,
              bgcolor: 'transparent',
              px: 2
            }}
          >
            <Box sx={{
              width: 56,
              height: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(90deg, rgba(255,0,192,1) 0%, rgba(255,109,21,1) 90%)',
              borderRadius: '50%',
              '& svg': { color: 'white', fontSize: 32 }
            }}>
              <AssessmentIcon />
            </Box>
          </Box>
          {/* Right column: Info */}
          <CardContent
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              p: 3,
              position: 'relative'
            }}
          >
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {description}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Chip
                icon={<TimeIcon sx={{ fontSize: 18 }}/>}
                label={`Duration: ~${duration}`}
                color="default"
                size="small"
                sx={{ fontWeight: 500 }}
              />
              {endDate && (
                <Chip
                  icon={<CalendarIcon sx={{ fontSize: 18 }}/>}
                  label={`${formatShortDate(startDate)} - ${formatShortDate(endDate)}`}
                  color="default"
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Modal Dialog as Component */}
        <AssessmentModal
          open={open}
          onClose={() => setOpen(false)}
          title={title}
          duration={duration}
          meetingSummary={meetingSummary}
          goals={goals}
          companyName={companyName}
          companyIndustry={companyIndustry}
          personaName={personaName}
          personaRole={personaRole}
          companyLocations={companyLocations}
          assignmentId={assignmentId}
          onStart={onStart}
          assessmentId={assessmentId}
          completeStatus={isCompleted}
          isAvailable={isAvailable}
        />
      </Box>
    );
  };

  export default AssessmentCard;